import {FC, useMemo, useRef} from "react";
import {Alert, Badge, Button, Card, Col, Modal, Row, Spinner} from "react-bootstrap";
import {useAppSelector} from "../../hooks";
import {selectAllEmployees, selectCalendar, selectCurrentEmployees, selectEmployeesStatus} from "../../../features/AppDataSlice";
import {dateToString, minutesToHoursAsString, showErrorsListInToast, timeDiffInMinutes, timeToString} from "../../tools";
import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import {RREventType} from "../../types/RREventType";
import FaceControl from "./FaceControl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExcel, faPrint} from "@fortawesome/free-solid-svg-icons";
import {useReactToPrint} from "react-to-print";
import {utils, writeFile} from "xlsx";

interface Props {
    event: RREventType;
    date: string;
    department: string;
    onClose: () => void;
}

const dayTypes: Record<RRCalendarDayType["dayType"], string> = {
    WORKDAY: "Работен ден", HOLIDAY: "Отпуск", DAYOFF: "Почивен ден", SICK: "Болничен",
};
const departments: Record<string, string> = {
    montage: "Монтаж", catering: "Кетъринг", store: "Кухня",
};
const faces: Record<string, string> = {
    GOOD: "Добра", NEUTRAL: "Неутрална", BAD: "Лоша", "NOT SET": "Не е поставена",
};

// При отчет, който завършва на следващ ден, показваме и датата до крайния час.
function reportTime(value: Date | null | undefined, date: string): string {
    if (!value) return "Не е отчетен";
    return dateToString(value) === date ? timeToString(value) : `${timeToString(value)} · ${dateToString(value)}`;
}

// Сумираме приключилите работни отчети след приспадане на почивките.
function workedMinutes(items: RRCalendarDayType[]): number {
    return items.reduce((sum, item) => sum + (item.dayType === "WORKDAY" && item.eventDayEnd ?
        Math.max(0, timeDiffInMinutes(item.eventDayStart, item.eventDayEnd, item.restTime || 0)) : 0), 0);
}

function reportPositions(item: RRCalendarDayType): string {
    return [item.isSupervisor ? "ОТГОВОРНИК" : "", item.isWarehouse ? "ОТГОВОРНИК СКЛАД" : "",
        item.isMontage && !item.isSupervisor ? "СЛУЖИТЕЛ" : ""].filter(Boolean).join(" | ");
}

const ReportStatus: FC<{item: RRCalendarDayType}> = ({item}) => (
    <span className="d-inline-flex align-items-center gap-2 text-nowrap">
        {item.isSupervisor && <>
            <Badge bg="light" text="dark">ОТГОВОРНИК</Badge>
            <span className="text-secondary" aria-hidden="true">|</span>
        </>}
        {item.isWarehouse && <>
            <Badge bg="light" text="dark">ОТГОВОРНИК СКЛАД</Badge>
            <span className="text-secondary" aria-hidden="true">|</span>
        </>}
        {item.isMontage && !item.isSupervisor && <>
            <Badge bg="light" text="dark">СЛУЖИТЕЛ</Badge>
            <span className="text-secondary" aria-hidden="true">|</span>
        </>}
        {item.isHoliday && <Badge bg="light" text="dark">Празничен ден</Badge>}
        <span className="small text-secondary">{dayTypes[item.dayType] || "Няма посочен тип ден"}</span>
        <span className="text-secondary" aria-hidden="true">|</span>
        <Badge pill bg={item.isConfirmed ? "success" : "light"} text={item.isConfirmed ? "white" : "dark"}>
            {item.isConfirmed ? "✓ Потвърден отчет" : "Непотвърден отчет"}
        </Badge>
    </span>
);

const EmployeeReport: FC<{item: RRCalendarDayType; date: string}> = ({item, date}) => {
    const showReportDetails = Boolean(item.eventDayEnd || item.restTime || item.bonus ||
        ["GOOD", "NEUTRAL", "BAD"].includes(item.face || ""));
    return (
    <>
        <Row className="g-2 mx-0 mb-3 flex-nowrap" style={{minWidth: showReportDetails ? 580 : 240}}>
            <Col>
                <div className="bg-light rounded p-3 h-100">
                    <div className="small text-secondary mb-1">Начало:</div>
                    <div className="fs-4 fw-semibold">{reportTime(item.eventDayStart, date)}</div>
                </div>
            </Col>
            <Col>
                <div className="bg-light rounded p-3 h-100">
                    <div className="small text-secondary mb-1">Край:</div>
                    <div className={item.eventDayEnd ? "fs-4 fw-semibold" : "text-secondary pt-2"}>
                        {reportTime(item.eventDayEnd, date)}
                    </div>
                </div>
            </Col>
            {showReportDetails && <>
            <Col>
                <div className="bg-light rounded p-3 h-100">
                    <div className="small text-secondary mb-1">Почивка:</div>
                    <div className="fs-4 fw-semibold text-nowrap">{item.restTime ? `${item.restTime} мин.` : ""}</div>
                </div>
            </Col>
            <Col>
                <div className="bg-light rounded p-3 h-100">
                    <div className="small text-secondary mb-1">Бонус:</div>
                    <div className="fs-4 fw-semibold">{item.bonus || ""}</div>
                </div>
            </Col>
            <Col>
                <div className="bg-light rounded p-3 h-100">
                    <div className="small text-secondary mb-1">Оценка:</div>
                    {item.face && ["GOOD", "NEUTRAL", "BAD"].includes(item.face) &&
                        <div className="fw-semibold" role="img" aria-label={faces[item.face]} title={faces[item.face]}>
                            <FaceControl face={item.face}/>
                        </div>}
                </div>
            </Col>
            </>}
        </Row>
        {item.description && <div className="mb-3">
            <div className="small text-secondary mb-1">Описание:</div>
            <div style={{whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}>{item.description}</div>
        </div>}
        {item.notes?.trim() && <div className="rounded p-3" style={{backgroundColor: "#f1f5f9"}}>
            <div className="small text-secondary mb-1">Забележки:</div>
            <div style={{whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}>
                {item.notes}
            </div>
        </div>}
    </>
    );
};

const EventDayReportsModal: FC<Props> = ({event, date, department, onClose}) => {
    const calendar = useAppSelector(selectCalendar);
    const employees = useAppSelector(selectAllEmployees);
    const calendarEmployees = useAppSelector(selectCurrentEmployees);
    const employeeStatus = useAppSelector(selectEmployeesStatus);
    const printRef = useRef<HTMLDivElement>(null);
    // Използваме текущите данни, за да отразим нови отчети и промени и при отворен диалог.
    const groups = useMemo(() => {
        const byEmployee = new Map<string, {name: string; items: RRCalendarDayType[]}>();
        calendar.data.filter(item => item.eventId === event.id && item.department === department &&
            dateToString(item.eventDayStart) === date).forEach(item => {
            const group = byEmployee.get(item.employeeId) || {
                name: employees.find(employee => employee.id === item.employeeId)?.name || item.employeeName || "Служител без посочено име",
                items: [],
            };
            group.items.push(item);
            byEmployee.set(item.employeeId, group);
        });
        // Календарът обхожда текущите служители в този ред; липсващите са след тях.
        const calendarOrder = new Map(calendarEmployees.map((employee, index) => [employee.id, index]));
        return Array.from(byEmployee.entries()).sort((a, b) => {
            const aSupervisor = a[1].items.some(item => item.isSupervisor || item.isWarehouse);
            const bSupervisor = b[1].items.some(item => item.isSupervisor || item.isWarehouse);
            if (aSupervisor !== bSupervisor) return aSupervisor ? -1 : 1;
            return (calendarOrder.get(a[0]) ?? calendarEmployees.length) -
                (calendarOrder.get(b[0]) ?? calendarEmployees.length);
        });
    }, [calendar.data, employees, calendarEmployees, event.id, date, department]);
    const loading = calendar.loading || employeeStatus.loading;
    const hasError = Boolean(calendar.error || employeeStatus.error);
    const totalMinutes = groups.reduce((sum, [, group]) => sum + workedMinutes(group.items), 0);
    const eventName = event.eventName || event.eventShortName || "Събитие";
    const outputTitle = `Отчети - ${eventName} - ${date}`;
    const outputDisabled = loading || hasError || groups.length === 0;
    const printReports = useReactToPrint({
        content: () => printRef.current,
        documentTitle: outputTitle,
        removeAfterPrint: true,
        pageStyle: `
            @page { size: A4 portrait; margin: 12mm; }
            @media print {
                html, body { height: auto !important; overflow: visible !important; }
                .event-day-reports-print { font-size: 12px; }
                .event-day-reports-print .card { break-inside: avoid; page-break-inside: avoid; }
                .event-day-reports-print .card-body { padding: 12px; }
                .event-day-reports-print .p-3 { padding: 10px !important; }
                .event-day-reports-print .fs-4 { font-size: 18px !important; }
                .event-day-reports-print .badge { color: black !important; border: 1px solid #ccc; }
                .event-day-reports-print [style*="overflow-x"] { overflow: visible !important; }
            }
        `,
        onPrintError: () => showErrorsListInToast("Грешка", ["Печатът не може да бъде подготвен. Опитайте отново."]),
    });

    // Експортираме същите служители и отчети в реда на картите, без технически полета.
    function exportReports(): void {
        if (outputDisabled) return;
        try {
            const rows: (string | number)[][] = [
                ["Събитие", eventName], ["Дата", date], ["Място", event.place || ""],
                ["Отдел", departments[department] || department],
                ["Общо изработено", minutesToHoursAsString(totalMinutes)],
                ["Служители", groups.length],
                ["Отчети", groups.reduce((sum, [, group]) => sum + group.items.length, 0)],
                ["Потвърдени отчети", groups.reduce((sum, [, group]) => sum + group.items.filter(item => item.isConfirmed).length, 0)],
                [],
                ["Служител", "Позиция", "Тип ден", "Потвърден отчет", "Начало", "Край", "Почивка (мин.)",
                    "Бонус", "Оценка", "Празничен ден", "Описание", "Забележки", "Изработено по отчета",
                    "Общо изработено от служителя"],
            ];
            groups.forEach(([, group]) => {
                group.items.slice().sort((a, b) => a.eventDayStart.getTime() - b.eventDayStart.getTime()).forEach(item => {
                    rows.push([group.name, reportPositions(item), dayTypes[item.dayType] || "Няма посочен тип ден",
                        item.isConfirmed ? "Да" : "Не", reportTime(item.eventDayStart, date), reportTime(item.eventDayEnd, date),
                        item.restTime, item.bonus, item.face ? faces[item.face] || "Не е поставена" : "Не е поставена",
                        item.isHoliday ? "Да" : "Не", item.description || "", item.notes || "",
                        minutesToHoursAsString(workedMinutes([item])), minutesToHoursAsString(workedMinutes(group.items))]);
                });
            });
            const sheet = utils.aoa_to_sheet(rows);
            sheet["!cols"] = [32, 28, 18, 22, 24, 24, 18, 12, 20, 18, 40, 40, 24, 32].map(wch => ({wch}));
            const workbook = utils.book_new();
            utils.book_append_sheet(workbook, sheet, "Отчети по служители");
            writeFile(workbook, `${outputTitle.replace(/[<>:"/\\|?*]/g, "-")}.xlsx`);
        } catch {
            showErrorsListInToast("Грешка", ["Експортът към Excel е неуспешен. Опитайте отново."]);
        }
    }

    return (
        <Modal show centered size="lg" scrollable backdrop="static" onHide={onClose}>
            <Modal.Header closeButton closeLabel="Затвори">
                <Modal.Title>Отчети по служители</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div ref={printRef} className="event-day-reports-print">
                <div className="mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <h5 className="mb-0">{event.eventName || event.eventShortName || "Събитие"}</h5>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <div className="d-flex flex-wrap gap-2 text-secondary small">
                                <span className="fw-semibold text-dark">{date}</span>
                                {event.place && <span>· {event.place}</span>}
                                <span>· {departments[department] || "Отдел"}</span>
                            </div>
                            {!loading && !hasError && <>
                                <span className="text-secondary" aria-hidden="true">|</span>
                                <span className="text-end text-nowrap">
                                    <span className="small text-secondary me-2">Общо изработено:</span>
                                    <b>{minutesToHoursAsString(totalMinutes)}</b>
                                </span>
                            </>}
                        </div>
                    </div>
                </div>
                {loading ?
                    <div role="status"><Spinner animation="border" size="sm"/> Зареждане на отчетите…</div> :
                    calendar.error || employeeStatus.error ?
                        <Alert variant="danger">Неуспешно зареждане: {calendar.error || employeeStatus.error}</Alert> :
                        groups.length === 0 ? <p>Няма отчети за това събитие за избрания ден и отдел.</p> : <>
                            <div className="d-flex flex-wrap gap-3 border-bottom pb-3 mb-3 small text-secondary">
                                <span><b className="text-dark">{groups.length}</b> служители</span>
                                <span><b className="text-dark">{groups.reduce((sum, [, group]) => sum + group.items.length, 0)}</b> отчета</span>
                                <span><b className="text-success">{groups.reduce((sum, [, group]) =>
                                    sum + group.items.filter(item => item.isConfirmed).length, 0)}</b> потвърдени</span>
                            </div>
                            <Row className="g-3">
                            {
                            groups.map(([employeeId, group]) => (
                                <Col key={employeeId} xs={12}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                                                <h6 className="fw-bold mb-0">{group.name}</h6>
                                                <div className="d-flex flex-wrap align-items-center gap-4">
                                                    <div className="d-flex flex-column gap-2">
                                                        {group.items.slice().sort((a, b) => a.eventDayStart.getTime() - b.eventDayStart.getTime())
                                                            .map((item, index) => (
                                                                <span key={item.id || index}>
                                                                    {group.items.length > 1 && <span className="small text-secondary me-2">Отчет {index + 1}:</span>}
                                                                    <ReportStatus item={item}/>
                                                                </span>
                                                            ))}
                                                    </div>
                                                    <span className="text-end text-nowrap border-start ps-4">
                                                        <span className="small text-secondary me-2">Изработено:</span>
                                                        <b>{minutesToHoursAsString(workedMinutes(group.items))}</b>
                                                    </span>
                                                </div>
                                            </div>
                                    {group.items.slice().sort((a, b) => a.eventDayStart.getTime() - b.eventDayStart.getTime())
                                        .map((item, index) => (
                                            <div key={item.id || index} className={index > 0 ? "border-top mt-4 pt-3" : ""}>
                                                {group.items.length > 1 && <div className="small fw-semibold mb-2">Отчет {index + 1}</div>}
                                                {item.employeeName && item.employeeName !== group.name &&
                                                    <p className="small text-secondary">Отчетен като: {item.employeeName}</p>}
                                                <div style={{overflowX: "auto"}}>
                                                    <EmployeeReport item={item} date={date}/>
                                                </div>
                                            </div>
                                        ))}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                            </Row>
                        </>}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex gap-2 me-auto">
                    <Button variant="outline-primary" disabled={outputDisabled} onClick={() => printReports()}>
                        <FontAwesomeIcon icon={faPrint}/> Печат
                    </Button>
                    <Button variant="outline-success" disabled={outputDisabled} onClick={exportReports}>
                        <FontAwesomeIcon icon={faFileExcel}/> Експорт към Excel
                    </Button>
                </div>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Затвори</button>
            </Modal.Footer>
        </Modal>
    );
};

export default EventDayReportsModal;
